"use client";

import { usePathname, useRouter } from "next/navigation";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { memo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { Hidden } from "@/components/ui/hidden";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { cn } from "@/lib/utils";
import { defaultEmptyText } from "@/constants";

import states from "@/static/states.json";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface SearchStateProps {
  filter: string;
}

const statePattern = /\/[a-zA-Z]{2}$/;

const List = ({ filter }: SearchStateProps) => {
  const router = useRouter();
  const pathname = usePathname();

  const url = pathname.replace(statePattern, "");

  return (
    <Command>
      <CommandInput placeholder="Buscar estado..." />
      <CommandList className="px-1">
        <CommandEmpty>{defaultEmptyText}</CommandEmpty>
        <CommandGroup heading="Estados">
          {states
            .sort((a, b) => a.pib - b.pib)
            .map((item, index) => {
              return (
                <CommandItem
                  key={`${item.value}-${index}`}
                  onSelect={() => {
                    // @ts-ignore
                    router.push(`${url}/${item.value}`);
                  }}
                >
                  {item.label}
                  <span className="uppercase ml-1">({item.value})</span>
                  <CheckIcon
                    className={cn(
                      "ml-auto size-4",
                      filter === item.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              );
            })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

export const SearchState = memo(({ filter }: SearchStateProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Hidden
      content={[
        <Sheet key="mobile">
          <SheetTrigger asChild>
            <Button
              variant="outline"
              aria-expanded={open}
              className="flex-1 justify-between w-full whitespace-normal"
            >
              {filter ? <span className="uppercase">{filter}</span> : "Estado"}
              <CaretSortIcon className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </SheetTrigger>
          <SheetContent
            aria-describedby={undefined}
            side="bottom"
            className="bg-white flex flex-col gap-10 overflow-auto rounded-tl-md rounded-tr-md w-full p-0 min-h-[70%] max-h-[70%]"
          >
            <VisuallyHidden asChild>
              <SheetTitle>Estados</SheetTitle>
            </VisuallyHidden>
            <div className="w-full flex flex-col gap-2 p-5 sm:p-7">
              <div className="flex items-center justify-end">
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Fechar"
                    className="p-2"
                  >
                    <XMarkIcon className="size-4" aria-hidden="true" />
                  </Button>
                </SheetClose>
              </div>
              <List filter={filter} />
            </div>
          </SheetContent>
        </Sheet>,
        <Popover key="desktop" open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="flex-1 justify-between w-full"
            >
              {filter ? <span className="uppercase">{filter}</span> : "Estado"}
              <CaretSortIcon className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0">
            <List filter={filter} />
          </PopoverContent>
        </Popover>,
      ]}
    />
  );
});

SearchState.displayName = "SearchState";
